import { useRetireEndedTables } from "@/hooks/useRetireEndedTables";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Clock3, UsersRound, MoreVertical, Menu as MenuIcon, Search, X } from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { usePOS } from "@/context/POSContext";
import { useCaptainAllTablesState } from "@/hooks/useCaptainOperationalState";
import { useActiveOrders } from "@/hooks/useActiveOrders";
import { NetworkIndicator } from "@/components/captain/NetworkIndicator";
import { CaptainAttendanceWidget } from "@/components/captain/CaptainAttendanceWidget";
import { tableTone, toneLabel } from "@/lib/occupancy";
import { elapsedFromSeconds, inr } from "@/lib/format";
import { billCardAmount, billCardNumber, localDraftOnly, unsentWarning } from "@/pages/billing/pendingBillCards";
import { EmptyState, LoadingState } from "@/components/ui/StateBoundaries";
import { meshCustomerPath, meshMenuPath, meshBillPath, meshKotsPath, getMeshBase } from "@/lib/captainMesh/routes";
import { CaptainCancelDialog } from "@/components/captain/CaptainCancelDialog";
import * as api from "@/lib/orderApi";
import { byBoardOrder, tileAspect, tileSpan } from "@/lib/tableOrder";
import CheckItemsPreview from "@/components/CheckItemsPreview";
// Imported under its own name: the component declares a one-argument
// `serverOrderOnTable(table)` below, which shadowed this import, so Check KOT's
// `(board, tableId)` call matched nothing and always said "no order on the server".
import { serverOrderOnTable as boardOrderForTable } from "@/lib/captainMesh/db";
import { TableOptionsSheet } from "@/components/captain/TableOptionsSheet";
import { useCaptainNetwork } from "@/hooks/useCaptainNetwork";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TableActionModals } from "@/components/captain/TableActionModals";
import { useOnScreenConfirm } from "@/hooks/useOnScreenConfirm";

const LIVE_STATE_LABEL = { open: "punching", kot_sent: "in kitchen", billing: "bill printed" };
const stateLabel = (running, tone) => LIVE_STATE_LABEL[running?.status] || toneLabel(tone);
const orNoTotal = value => (value === null || value === undefined || value === "" ? "—" : inr(value));

export default function CaptainTables() {
  const { tables, areas, areasHydrated, areaTables, orderBusy, token, subscribeRealtime, settings } = usePOS();
    const { orderForTable: meshOrderForTable } = useCaptainAllTablesState();
    const { orders: restOrders, loaded: restLoaded } = useActiveOrders(token, subscribeRealtime);
    // Tables another device ended stop showing here (2026-10-24).
    useRetireEndedTables(restOrders, restLoaded, token);
    
    const serverOrderOnTable = table =>
        restOrders.find(row => row.table_id === table.id && row.status !== "billing") || null;

    const orderForTable = (tableId) => {
        const meshOrder = meshOrderForTable(tableId);
        const table = tables?.find(t => t.id === tableId);
        // By table id only: numbers repeat across areas, so matching on the
        // number painted another area's "2" onto Garden 2.
        const restOrder = table ? serverOrderOnTable(table) : null;

        if (meshOrder && restOrder) {
            return {
                ...meshOrder,
                ...restOrder,
                table_id: meshOrder.table_id || restOrder.table_id || tableId,
                aggregate_id: meshOrder.aggregate_id,
                amount: restOrder.amount ?? restOrder.total ?? meshOrder.amount,
                elapsed_seconds: restOrder.elapsed_seconds ?? meshOrder.elapsed_seconds,
                guest_count: restOrder.guest_count ?? meshOrder.guest_count,
                status: (restOrder.status && restOrder.status !== "billing") ? restOrder.status : meshOrder.status,
            };
        }
        // Connected and the board read: a copy the server has nothing running
        // for shows only while it holds items punched here and not yet sent
        // (pendingBillCards.localDraftOnly).
        if (meshOrder && meshOrder.status !== "billing" && !(isOnline && restLoaded && !localDraftOnly(meshOrder))) return meshOrder;
        if (restOrder) return restOrder;
        return null;
    };
  const navigate = useNavigate();
  // Supplied by CaptainLayout's <Outlet context>; the hamburger lives in this
  // screen's header but the drawer belongs to the layout.
  const { openDrawer, toggleDesktopSidebar } = useOutletContext();

  const [search, setSearch] = useState("");
  const [actionTable, setActionTable] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  const { isOnline } = useCaptainNetwork();
  const [pendingBills, setPendingBills] = useState([]);

  const fetchPending = useCallback(async () => {
    if (!token) return;
    try {
      const bills = await api.listOrders({ status: "billing" }, token);
      setPendingBills(bills); console.log("Fetched pending bills:", bills);
    } catch (e) {}
  }, [token, isOnline]);

  useEffect(() => {
    fetchPending();
    const timer = setInterval(fetchPending, 15000);
    return () => clearInterval(timer);
  }, [fetchPending]);


  // We group and filter at the same time
  const { groups: areaGroups, tileCount } = useMemo(() => {
    const floor = tables || [];
    const searchLower = search.toLowerCase();
    
    // Filter floor first based on search
    const filteredFloor = floor.filter(t => 
      t.name?.toLowerCase().includes(searchLower) || 
      t.number?.toString().includes(searchLower)
    );

    const grouped = [];
    const placed = new Set();

    (areas || []).forEach(area => {
      const zoneTables = (areaTables || []).filter(at => at.zone === area.id);
      const matchedTables = zoneTables
        .map(at => filteredFloor.find(t => t.number === at.number))
        .filter(Boolean)
        .filter(t => !placed.has(t.id));
      matchedTables.forEach(t => placed.add(t.id));
      // Saved position (Settings → Tables), then number, numerically.
      matchedTables.sort(byBoardOrder);
      
        const expandedTables = [];
        matchedTables.forEach(t => {
          expandedTables.push(t);
          if (getMeshBase() === '/billing') {
            const billsForTable = (pendingBills || []).filter(b => b.table_number == t.number);
            billsForTable.forEach(b => {
              expandedTables.push({
                ...t,
                id: "pending-" + b.id,
                isPendingBillCard: true,
                pendingBill: b,
                name: (t.name || "Table") + " (Bill)"
              });
            });
          }
        });
        if (expandedTables.length) grouped.push({ ...area, tables: expandedTables });
    });

    const unassigned = filteredFloor
      .filter(t => !placed.has(t.id))
      .sort(byBoardOrder);
    if (unassigned.length) {
      const expandedUnassigned = [];
        unassigned.forEach(t => {
          expandedUnassigned.push(t);
          if (getMeshBase() === '/billing') {
            const billsForTable = (pendingBills || []).filter(b => b.table_number == t.number);
            billsForTable.forEach(b => {
              expandedUnassigned.push({
                ...t,
                id: "pending-" + b.id,
                isPendingBillCard: true,
                pendingBill: b,
                name: (t.name || "Table") + " (Bill)"
              });
            });
          }
        });
        grouped.push({ id: "__unassigned__", name: "Unassigned", tables: expandedUnassigned });
    }

    const count = grouped.reduce((n, g) => n + g.tables.length, 0);
    if (!count && filteredFloor.length) {
      const all = [...filteredFloor].sort(byBoardOrder);
      const expandedAll = [];
        all.forEach(t => {
          expandedAll.push(t);
          if (getMeshBase() === '/billing') {
            const billsForTable = (pendingBills || []).filter(b => b.table_number == t.number);
            billsForTable.forEach(b => {
              expandedAll.push({
                ...t,
                id: "pending-" + b.id,
                isPendingBillCard: true,
                pendingBill: b,
                name: (t.name || "Table") + " (Bill)"
              });
            });
          }
        });
        return { groups: [{ id: "__all__", name: null, tables: expandedAll }], tileCount: expandedAll.length };
    }
    return { groups: grouped, tileCount: count };
  }, [areas, areaTables, tables, search, pendingBills]);


  // §39.2 abandonment: a draft that reaches this board with nothing on it — no
  // items, no KOT ever requested — is an order that came to nothing, and it
  // must not hold its table "punching" for the rest of service. The board is
  // the commit point on purpose: while the captain is still on the menu or
  // customer screens the empty state is mid-edit, not abandoned.
  useEffect(() => {
    if (!token) return;
    let stale = false;
    (async () => {
      const { sweepAbandonedDrafts } = await import("@/lib/captainMesh/db");
      if (stale) return;
      await sweepAbandonedDrafts({
        isOnline,
        fetchOrder: id => api.getOrder(id, token),
        fetchItems: id => api.getOrderItems(id, token),
        cancelOnServer: (id, version) =>
          api.cancelOrder(
            id,
            { reason: "Abandoned: nothing was punched" },
            version,
            token
          ),
      }).catch(() => {});
    })();
    return () => { stale = true; };
  }, [token, isOnline]);

  // §39.3 board-level reconciliation: every time the server's live board
  // changes (it refreshes on each order_update over the socket), bring this
  // device's mapped aggregates in line with it. An order the board no longer
  // lists is confirmed with the server and its ending recorded, so the table
  // frees; an order the board lists on ANOTHER table moved there, and the
  // mapping says so. This used to run once, when the screen mounted — a captain
  // who stayed on the tables screen kept every move, merge, cancel and bill
  // made elsewhere off their pad until they navigated away and back.
  const boardKey = restOrders.map(o => `${o.id}:${o.table_id}:${o.status}`).join("|");
  useEffect(() => {
    if (!token || !isOnline) return;
    let stale = false;
    (async () => {
      try {
        const { saveOrderMapping } = await import("@/lib/captainMesh/db");
        const { offlineDb } = await import("@/db/offlineDb");
        const mappings = await offlineDb.mesh_order_map.toArray();
        const LIVE = new Set(["open", "kot_sent", "billing"]);
        const liveMappings = mappings.filter(m => m.order_id && (!m.order_status || LIVE.has(m.order_status)));
        if (!liveMappings.length || stale) return;

        // A fresh read, not the hook's copy: the decision frees tables.
        const board = await api.listActiveOrders(token);
        const byId = new Map(board.map(o => [o.id, o]));

        for (const m of liveMappings) {
          if (stale) return;
          const serverOrder = byId.get(m.order_id);
          if (serverOrder) {
            if (serverOrder.table_id != null && serverOrder.table_id !== m.table_id) {
              await saveOrderMapping(m.aggregate_id, { order_id: m.order_id, table_id: serverOrder.table_id });
            }
            if (serverOrder.status && serverOrder.status !== m.order_status) {
              await saveOrderMapping(m.aggregate_id, { order_id: m.order_id, order_status: serverOrder.status });
            }
            continue;
          }
          // Not on the live board: closed, cancelled or merged away elsewhere.
          // Ask the server rather than assume, then record its answer.
          try {
            const order = await api.getOrder(m.order_id, token);
            await saveOrderMapping(m.aggregate_id, { order_id: m.order_id, order_status: order.status });
          } catch (err) {
            if (err?.response?.status === 404) {
              await saveOrderMapping(m.aggregate_id, { order_id: m.order_id, order_status: "closed" });
            }
            // Anything else (network): leave it; the next board change retries.
          }
        }
      } catch {
        // Best-effort; the next board change retries.
      }
    })();
    return () => { stale = true; };
  }, [token, isOnline, boardKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [optionsFor, setOptionsFor] = useState(null);
  const [checkPreview, setCheckPreview] = useState(null);
  const holdTimer = useRef(null);
  const heldRef = useRef(false);

  const handleTargetSelection = async (targetTable) => {
    const sourceOrder = orderForTable(actionTable.id);
    const targetOrder = orderForTable(targetTable.id);
    
    if (targetTable.id === actionTable.id) {
      toast.error("Target table cannot be the same as the source table.");
      return;
    }

    const sourceServerId = sourceOrder?.server_order_id || sourceOrder?.id;
    const targetServerId = targetOrder?.server_order_id || targetOrder?.id;

    if (!sourceServerId) {
      toast.error("Source order is not synced to server yet.");
      return;
    }

    try {
      // Re-read the versions from the server every time. The board row can be
      // stale, and on the pad it is the device's own copy with no server version
      // at all — the move went out with no If-Match and the server answered 428
      // "If-Match header required" (C05, order 765, 2026-10-13 17:13 IST).
      const sourceVersion = (await api.getOrder(sourceServerId, token)).version;
      if (actionType === 'select_target_shift') {
        if (targetServerId) {
          const targetVersion = (await api.getOrder(targetServerId, token)).version;
          await api.mergeOrders(targetServerId, sourceServerId, targetVersion, token, sourceVersion);
          // The source order is cancelled by the merge: record that on its
          // mapping so the source table frees here. (This used to delete the
          // first projection found by table id too, which could be a different
          // aggregate, and the next re-fold rebuilt it anyway.)
          const { saveOrderMapping } = await import("@/lib/captainMesh/db");
          if (sourceOrder?.aggregate_id) {
            await saveOrderMapping(sourceOrder.aggregate_id, { order_id: sourceServerId, order_status: "cancelled" });
          }
          toast.success(`Merged ${actionTable.name} into ${targetTable.name}`);
        } else {
          await api.transferOrder(sourceServerId, targetTable.id, sourceVersion, token);
          // The server's table goes on the mapping, which outranks the fold.
          // Writing it into the materialized row was undone by the next
          // re-fold: the source table went back to "occupied" on this pad.
          const { saveOrderMapping } = await import("@/lib/captainMesh/db");
          if (sourceOrder?.aggregate_id) {
            await saveOrderMapping(sourceOrder.aggregate_id, { order_id: sourceServerId, table_id: targetTable.id });
          }
          toast.success(`Moved ${actionTable.name} to ${targetTable.name}`);
        }
      } else if (actionType === 'select_target_items') {
        if (!selectedItemIds || selectedItemIds.size === 0) {
          toast.error("Please select at least one item to move.");
          return;
        }
        // Server rows move; the server's reconcile counts rows this device sent
        // wherever they now live, so the next punch here never re-adds them.
        await api.transferItems(sourceServerId, Array.from(selectedItemIds), targetTable.id, sourceVersion, token);
        toast.success(`Moved items to ${targetTable.name}`);
      }

      setActionType(null);
      setSelectedItemIds(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Action failed");
    }
  };

  const handleClick = async (table) => {
    const running = meshOrderForTable(table.id);

    if (actionType === 'select_target_shift' || actionType === 'select_target_items') {
      await handleTargetSelection(table);
      return;
    }

    if (false) {
      if (!running) {
        toast.error("Cannot move. This table has no active order yet.");
        return;
      }
      if (running.status === "billing" || running.status === "settled") {
        toast.error("Cannot move items from a billed or settled order.");
        return;
      }
      setActionTable(table);
      setActionType("select_action");
      return;
    }

    // Navigation Action ONLY. Do not create an order.
    
    // Check if the table has a pending bill (since tables are released on print,
    // the bill might be floating waiting for settlement)
    const pending = pendingBills.find(b => b.table_id == table.id || b.table_number == table.number);
    
    if (table.isPendingBillCard) {
      navigate(`${getMeshBase()}/bill/${table.pendingBill.id}`);
      return;
    }

    const serverOccupied = !!serverOrderOnTable(table);
    if (running) {
      // Locally-known order: straight to the catalogue.
      navigate(meshMenuPath(table.id));
    } else if (serverOccupied) {
      // The server holds an order this device has no aggregate for (opened on
      // the till, or before this device's IndexedDB existed). Single tap must
      // enter the order-editing workflow like any other occupied table - so
      // ADOPT the server order into local state (acked events, nothing sent)
      // and open the catalogue on it. Options belong to long-press only.
      const adopted = await adoptIfNeeded(table);
      if (adopted) navigate(meshMenuPath(table.id));
    } else {
      // Empty table, must collect customer details first
      navigate(meshCustomerPath(table.id));
    }
  };

  // Teach this device about a server-side order so any order workflow (menu,
  // edit-customer, summary) can run on it. True when an aggregate exists after
  // the call; false means the caller should not navigate.
  const adoptIfNeeded = async table => {
    if (meshOrderForTable(table.id)) return true;
    if (!isOnline || !token) {
      toast.error(`${table.name} is served from another terminal — reconnect to edit it here`);
      return false;
    }
    try {
      const board = await api.listActiveOrders(token);
      const row = board.find(o => o.table_id === table.id);
      if (!row) {
        toast.error(`${table.name} has no running order on the server`);
        return false;
      }
      const [order, items] = await Promise.all([
        api.getOrder(row.id, token),
        api.getOrderItems(row.id, token),
      ]);
      let customer = null;
      if (order.customer_id) {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/customers/${order.customer_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.ok) customer = await res.json();
        } catch { customer = null; }
      }
      const { adoptServerOrder } = await import("@/lib/captainMesh/db");
      const { uuid } = await import("@/lib/uuid");
      await adoptServerOrder({ order, items, customer, tableDbId: table.id, deviceId: null, makeId: uuid });
      return true;
    } catch {
      toast.error(`Could not open ${table.name} — try again`);
      return false;
    }
  };

  // Long-press -> options. Pointer events cover touch and mouse alike; the tap
  // handler checks heldRef so releasing after a hold does not ALSO navigate.
  const holdStart = (table) => {
    heldRef.current = false;
    holdTimer.current = setTimeout(() => {
      heldRef.current = true;
      const running = orderForTable(table.id);
      if (running || serverOrderOnTable(table)) setOptionsFor(table);
    }, 500);
  };
  const holdEnd = () => clearTimeout(holdTimer.current);

  const optionsRunning = optionsFor ? orderForTable(optionsFor.id) : null;
  // A local claim the server does not share is a stale lease ONLY when the
  // server can be asked and the claim holds nothing to lose. Offline, every
  // capture is unmapped and every table looks free to the server - the old rule
  // (`!isOnline || ...`) therefore wiped each offline order the moment its
  // captain long-pressed the table: C05's FIRE KOT on 2 Gardenn became CONFLICT
  // with nothing ever sent (2026-10-19 20:53). A claim with lines waits for the
  // sync loop, which delivers it or reports a real conflict.
  const optionsLiveLines = optionsRunning
    ? Object.values(optionsRunning.items || {}).filter(i => i.status !== "cancelled" && i.status !== "removed")
    : [];
  const optionsStale =
    !!optionsRunning &&
    // A mapped aggregate IS a real server order - never a stale lease, whatever
    // the (possibly stale) cached table row says about the table.
    !optionsRunning.server_order_id &&
    optionsRunning.status !== "CONFLICTED" &&
    isOnline &&
    !["occupied", "billing"].includes(optionsFor?.status) &&
    optionsLiveLines.length === 0;
  const optionsStaleAgg = optionsStale ? optionsRunning.order_id : null;

  // Auto-clear an empty stale claim - no UI confirm needed, nothing is lost.
  // An effect, not render-time code: the render path re-ran it on every paint.
  useEffect(() => {
    if (!optionsStaleAgg) return;
    const tableName = optionsFor?.name;
    (async () => {
      try {
        const { markAggregateRejected, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
        // Rejected takes its events out of the flush queue; discarded then frees
        // the table instead of leaving it on CONFLICT.
        await markAggregateRejected(optionsStaleAgg);
        await markAggregateDiscarded(optionsStaleAgg);
        toast.success(`${tableName} is available again`);
      } catch (err) {
        toast.error(`Could not free ${tableName}: ${err?.message || err}`);
      } finally {
        setOptionsFor(null);
      }
    })();
  }, [optionsStaleAgg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Our own confirm, not window.confirm: see useOnScreenConfirm.
  const { confirmOnScreen, confirmDialog } = useOnScreenConfirm("captain-confirm");

  const reclaim = async () => {
    const agg = optionsRunning?.order_id;
    const tableName = optionsFor?.name;
    setOptionsFor(null);
    if (!agg) return;
    const lines = Object.values(optionsRunning.items || {}).filter(
      i => i.status !== "cancelled" && i.status !== "removed"
    );
    const ok = await confirmOnScreen(
      `Clear this device's claim on ${tableName}?`,
      lines.length ? `${lines.length} unsent item(s) on it will be discarded.` : "Nothing was punched on it.",
      "Clear claim"
    );
    if (!ok) return;
    const { markAggregateRejected, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
    await markAggregateRejected(agg);
    await markAggregateDiscarded(agg);
    console.warn("[CaptainPAD] table claim reclaimed", { table: tableName, aggregate: agg, discarded_lines: lines.length });
    toast.success(`${tableName} is available again`);
  };

  // Cancel Table: confirmed by the user's PIN in CaptainCancelDialog.
  const [cancelFor, setCancelFor] = useState(null);

  const openCancel = async table => {
    setOptionsFor(null);
    const running = orderForTable(table.id);
    let serverId = running?.server_order_id ?? null;
    let orderNumber = running?.order_number ?? null;
    if (!serverId && token) {
      try {
        const board = await api.listActiveOrders(token);
        const row = board.find(o => o.table_id === table.id);
        serverId = row?.id ?? null;
        // The REAL order number rides with the id - "Cancel order #undefined"
        // was this lookup not happening.
        orderNumber = row?.order_number ?? null;
      } catch { serverId = null; }
    }
    if (!serverId) {
      if (running) {
        const ok = await confirmOnScreen(
          `Free ${table.name}?`,
          `There is no active order on the server for ${table.name}. This device's local draft will be discarded.`,
          "Discard and free"
        );
        if (!ok) return;
        try {
          const { markAggregateRejected, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
          if (running.order_id) {
            await markAggregateRejected(running.order_id);
            if (markAggregateDiscarded) await markAggregateDiscarded(running.order_id);
          }
          toast.success(`${table.name} is available again`);
        } catch (err) {
          toast.error(`Could not free ${table.name}: ${err?.message || err}`);
        }
        return;
      }
      const ok = await confirmOnScreen(
        `Release ${table.name}?`,
        `${table.name} has no running order on the server. It will be marked available.`,
        "Release table"
      );
      if (ok) {
        try {
          await api.releaseTable(table.id, token);
          toast.success(`${table.name} is now available`);
        } catch (err) {
          toast.error(`Could not release ${table.name}: ${err?.response?.data?.detail || err?.message || err}`);
        }
      }
      return;
    }
    setCancelFor({ table, serverId, running, orderNumber });
  };

  const confirmCancelTable = async ({ reasonCode, reason, authorizedBy }) => {
    const { table, serverId, running } = cancelFor;
    try {
      const fresh = await api.getOrder(serverId, token);
      let result = { void_slips: 0 };
      if (fresh.status !== "cancelled" && fresh.status !== "closed") {
        result = await api.cancelOrder(serverId, { reasonCode, reason, authorizedBy }, fresh.version, token);
      }
      if (running) {
        const { saveOrderMapping, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
        await saveOrderMapping(running.order_id, { order_id: serverId, order_status: "cancelled" });
        if (markAggregateDiscarded) await markAggregateDiscarded(running.order_id);
      }
      setCancelFor(null);
      toast.success(
        `${table.name} cancelled` + (result.void_slips ? ` · ${result.void_slips} VOID slip(s) sent` : "")
      );
      return "done";
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && (detail.toLowerCase().includes("cancelled") || detail.toLowerCase().includes("transition"))) {
        if (running) {
          const { saveOrderMapping, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
          await saveOrderMapping(running.order_id, { order_id: serverId, order_status: "cancelled" });
          if (markAggregateDiscarded) await markAggregateDiscarded(running.order_id);
        }
        setCancelFor(null);
        toast.success(`${table.name} cancelled`);
        return "done";
      }
      throw e;
    }
  };

  // The testid stays mesh-specific: CaptainPage carried "captain-table-screen"
  // too, so an e2e wait on that matched whichever page happened to be mounted.
  return (
    <div className="flex flex-col h-full bg-background relative" data-testid="captain-mesh-table-screen">
      
      {/* HEADER */}
      <header className="min-h-[3.5rem] pt-safe flex items-center justify-between px-4 bg-primary text-primary-foreground shadow-md sticky top-0 z-20 shrink-0 gap-3">
        {/* Mobile menu button */}
        <button
          className="w-11 h-11 -ml-2 flex items-center justify-center rounded-md hover:bg-primary-foreground/20 lg:hidden"
          onClick={openDrawer}
          aria-label="Open mobile menu"
          data-testid="captain-menu-button-mobile"
        >
          <MenuIcon className="w-6 h-6" />
        </button>

        {/* Desktop sidebar toggle button */}
        {toggleDesktopSidebar && (
          <button
            className="w-11 h-11 -ml-2 hidden lg:flex items-center justify-center rounded-md hover:bg-primary-foreground/20"
            onClick={toggleDesktopSidebar}
            aria-label="Toggle desktop menu"
            data-testid="captain-menu-button-desktop"
          >
            <MenuIcon className="w-6 h-6" />
          </button>
        )}
        
        <div className="flex-1 max-w-sm relative flex items-center">
          <Search className="w-4 h-4 absolute left-3 text-muted-foreground" />
          <input
            type="text"
            className="w-full h-9 pl-9 pr-3 rounded-full bg-card border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 transition-all"
            placeholder="Search tables..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {actionType === 'select_target_shift' || actionType === 'select_target_items' ? (
            <Button variant="destructive" size="icon" className="rounded-full w-8 h-8" onClick={() => { setActionType(null); setActionTable(null); setSelectedItemIds(new Set()); }}>
              <X className="w-5 h-5" />
            </Button>
          ) : (
            <>
              <CaptainAttendanceWidget token={token} />
              <NetworkIndicator />
            </>
          )}
          <h1 className="text-title font-bold tracking-wide hidden sm:block">CaptainPAD</h1>
        </div>
      </header>

      {/* GRID */}
      {actionType === 'select_target_shift' || actionType === 'select_target_items' ? (
        <div className="bg-primary/10 text-primary px-base py-3 flex justify-between items-center shrink-0 border-b border-primary/20">
          <div className="flex flex-col">
            <strong className="text-sm font-bold">Select destination for {actionTable?.name}</strong>
            <small className="text-xs opacity-80">Click a table below to complete the transfer</small>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setActionType(null)}>Cancel</Button>
        </div>
      ) : (
        <div className="px-base pt-snug text-micro font-bold uppercase text-muted-foreground/70 shrink-0">
          {actionType ? "Select a destination table" : "Tap to open · hold an occupied table for options"}
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-base space-y-loose pb-section">
        {areaGroups.map(area => (
          <div key={area.id} className="space-y-3">
            {area.name && <h2 className="text-body font-bold text-muted-foreground uppercase tracking-widest">{area.name}</h2>}
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 auto-rows-[minmax(6rem,auto)] grid-flow-dense gap-2" data-testid="table-grid">
              {area.tables.map((table, i) => {
                 let tone = "vacant";
                   let running = null;
                   if (table.isPendingBillCard) {
                       tone = "billing";
                   } else {
                       running = orderForTable(table.id);
                       // Connected and the board read: the live orders are the truth. The
                       // table's cached status is from this device's last bootstrap and never
                       // refreshed, so a table freed since stayed blue (owner, 2026-10-26).
                       tone = tableTone(running, isOnline && restLoaded ? null : table);
                   }


                 // Status speaks through the dot, border and label - never as the
                 // body text colour: dark surfaces turned status-coloured text
                 // illegible, which is a POS failure dressed as a theme.
                 // Semantic tokens, same meanings (owner, 2026-10-25): success = free,
                 // info = live work, warning = waiting on money, danger = conflict.
                 // Dark mode comes from the tokens, not per-class `dark:` overrides.
                 let colorClasses = "border-success/40 bg-card text-foreground";
                 let indicatorColor = "bg-success";
                 let labelColor = "text-success";
                 if (tone === "occupied") {
                   colorClasses = "border-info bg-info-subtle text-foreground shadow-sm";
                   indicatorColor = "bg-info";
                   labelColor = "text-info";
                 }
                 if (tone === "billing") {
                   colorClasses = "border-warning bg-warning-subtle text-foreground shadow-sm";
                   indicatorColor = "bg-warning";
                   labelColor = "text-warning";
                 }

                 if (tone === "conflicted") {
                   colorClasses = "border-danger bg-danger-subtle text-foreground shadow-sm";
                   indicatorColor = "bg-danger";
                   labelColor = "text-danger";
                 }
                 let disabledOpacity = ""; let isDisabled = false;
                 const isTargetSelection = actionType === 'select_target_shift' || actionType === 'select_target_items';
                 
                 if (isTargetSelection) {
                   if (table.id === actionTable?.id || tone === "billing" || running?.status === "settled") {
                     disabledOpacity = "opacity-50 grayscale cursor-not-allowed"; isDisabled = true;
                   }
                 } else if (false) {
                   if (tone === "vacant" || tone === "billing" || running?.status === "settled") {
                     disabledOpacity = "opacity-50 grayscale cursor-not-allowed"; isDisabled = true;
                   }
                 }

                 return (
                   <div key={table.id} className="relative animate-stagger-pop" style={{ animationDelay: `${i * 30}ms`, ...tileSpan(table) }}>
                     <button
                        style={{ aspectRatio: tileAspect(table) }}
                        className={`w-full h-full relative flex flex-col items-center justify-center px-2 py-2 rounded-lg border-2 transition-transform active:scale-95 ${colorClasses} ${disabledOpacity}`}
                        onClick={() => { if (heldRef.current) { heldRef.current = false; return; } handleClick(table); }}
                        onPointerDown={() => holdStart(table)}
                        onPointerUp={holdEnd}
                        onPointerLeave={holdEnd}
                        onContextMenu={e => {
                          // Desktop's long-press: right-click opens the same
                          // options sheet (suppressing the browser menu either
                          // way — during a touch long-press this event fires too).
                          e.preventDefault();
                          if (running || tone === "occupied" || tone === "billing") setOptionsFor(table);
                        }}
                        // One `disabled`: there used to be two, and the later
                        // `{orderBusy}` silently replaced `{isDisabled}`, so a greyed-out
                        // table could still be picked as a move destination.
                        disabled={isDisabled || orderBusy} data-tone={tone}
                        data-testid={`table-${table.name}-button`}
                     >
                       <div className="absolute top-1.5 left-0 w-full px-2 flex justify-between items-start text-micro font-bold opacity-80 pointer-events-none">
                           <div className="flex items-center gap-1.5">
                             {running && (
                               <span className="flex items-center gap-0.5 tabular-nums tracking-tighter">
                                 <Clock3 className="w-3 h-3"/>{elapsedFromSeconds(running.elapsed_seconds)}
                               </span>
                             )}
                           </div>
                           <span className="flex items-center gap-1">
                             <span className="flex items-center gap-0.5">
                               <UsersRound className="w-3 h-3"/>{running ? running.guest_count : table.isPendingBillCard ? table.pendingBill.guest_count : table.covers}
                             </span>
                             {table.isPendingBillCard && billCardNumber(table.pendingBill) && (
                               <span data-testid={`bill-card-${table.pendingBill.id}-number`}>{billCardNumber(table.pendingBill)}</span>
                             )}
                           </span>
                         </div>
                         <strong className="text-title font-bold tracking-tight mt-4 w-min mx-auto text-center px-1 leading-[1.1]">{table.name}</strong>
                         {running && orNoTotal(running.amount) !== "—" && (
                           <span className="font-bold tabular-nums text-xs opacity-90 leading-tight">
                             {orNoTotal(running.amount)}
                           </span>
                         )}
                         {table.isPendingBillCard && orNoTotal(billCardAmount(table.pendingBill)) !== "—" && (
                           <span className="font-bold tabular-nums text-xs opacity-90 leading-tight" data-testid={`bill-card-${table.pendingBill.id}-amount`}>
                             {orNoTotal(billCardAmount(table.pendingBill))}
                           </span>
                         )}
                         {unsentWarning(running, settings?.unsent_items_warn_minutes) ? (
                           // Punched but not sent to the kitchen (2026-10-24): said
                           // where the captain looks, in place of the state word.
                           <small className="mt-0.5 px-1 rounded border border-danger/40 bg-danger-subtle text-danger text-micro font-bold" data-testid={`table-${table.name}-unsent`}>
                             {unsentWarning(running, settings?.unsent_items_warn_minutes)}
                           </small>
                         ) : (
                           <small className={`mt-0.5 text-micro uppercase font-black tracking-widest ${labelColor}`}>
                             {running ? stateLabel(running, tone) : toneLabel(tone)}
                           </small>
                         )}
                     </button>
                   </div>
                 );
              })}
            </div>
          </div>
        ))}

        {!tileCount && (
          !areasHydrated
            ? <LoadingState message="Reading the floor plan…" testId="captain-tables-loading" />
            : <EmptyState
                title={search ? "No tables found" : "No tables"}
                message={search ? "No tables match your search query." : "No tables have been loaded."}
                testId="captain-tables-empty"
              />
        )}
      </div>

      {checkPreview && (
        <CheckItemsPreview orderId={checkPreview.orderId} tableName={checkPreview.tableName} token={token}
          onClose={() => setCheckPreview(null)} />
      )}
      {optionsFor && (
        <TableOptionsSheet
          table={optionsFor}
          onMoveKot={() => { const r = orderForTable(optionsFor.id); if(!r){ toast.error('No active order'); return; } if(r.status==='billing'||r.status==='settled'){ toast.error('Order is frozen at billing'); return; } setActionTable(optionsFor); setActionType('select_action'); setOptionsFor(null); }}
          onViewKots={() => { setOptionsFor(null); navigate(meshKotsPath(optionsFor.id)); }}
          onCheckKot={async () => {
            const t = optionsFor;
            setOptionsFor(null);
            try {
              const board = await api.listActiveOrders(token);
              const order = boardOrderForTable(board, t.id);
              if (!order) { toast.error(`${t.name} has no order on the server yet`); return; }
              setCheckPreview({ orderId: order.id, tableName: t.name });
            } catch (err) {
              toast.error(`Check not opened — ${err?.message || "the server did not answer"}`);
            }
          }}
          onPrintBill={() => { setOptionsFor(null); navigate(meshBillPath(optionsFor.id)); }}
          onEditCustomer={async () => { const t = optionsFor; setOptionsFor(null); if (await adoptIfNeeded(t)) navigate(`${meshCustomerPath(t.id)}?edit=1`); }}
          onCancelTable={() => openCancel(optionsFor)}
          onReclaim={optionsRunning && !optionsRunning.server_order_id ? reclaim : (optionsStale ? reclaim : null)}
          reclaimHint={optionsRunning && !optionsRunning.server_order_id ? "Discard unsent order and free table" : (optionsStale ? "Release table occupied only on this device" : null)}
          onClose={() => setOptionsFor(null)}
        />
      )}

      {confirmDialog}

      {cancelFor && (
        <CaptainCancelDialog
          orderNumber={cancelFor.orderNumber}
          tableName={cancelFor.table?.name}
          onConfirm={confirmCancelTable}
          onClose={() => setCancelFor(null)}
        />
      )}

      {actionType && (
        <TableActionModals 
          actionTable={actionTable}
          actionType={actionType}
          setActionType={setActionType}
          selectedItemIds={selectedItemIds}
          setSelectedItemIds={setSelectedItemIds}
          token={token}
          tables={tables}
          orderForTable={orderForTable}
          onSuccess={() => {
            setActionType(null);
          }}
        />
      )}
    </div>
  );
}
